import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { BRAND, SERVICE_OPTIONS, IMAGES } from "../../lib/site";
import { Reveal } from "../../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  service: SERVICE_OPTIONS[0],
  preferred_date: "",
  preferred_time: "",
  notes: "",
  website: "",
};

export const Booking = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.preferred_date || !form.preferred_time) {
      toast.error("Please fill in your name, phone, date and time.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Booking request sent! We'll confirm by phone shortly.");
      setForm(EMPTY);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 429) {
        toast.error("Too many requests from your connection. Please call us to book.");
      } else {
        toast.error("Something went wrong. Please call us to book.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#2c1e16]/20 rounded-none py-3 font-body text-[#2c1e16] placeholder:text-[#2c1e16]/40 focus:outline-none focus:border-[#c96c31] transition-colors";

  return (
    <section id="book" className="py-24 md:py-32 bg-[#f3ebe1]" data-testid="booking-section">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-[#c96c31] mb-5">
                Reserve Your Seat
              </p>
              <h2 className="font-display font-light leading-[0.95] tracking-tight text-[#2c1e16] text-4xl sm:text-5xl lg:text-6xl">
                Book your<br />
                <span className="italic text-[#c96c31]">elevation.</span>
              </h2>
              <p className="mt-6 font-body text-base md:text-lg text-[#2c1e16]/70 leading-relaxed max-w-md">
                Request an appointment below and we{"\u2019"}ll confirm your time by phone. Prefer to talk? Call us directly — we{"\u2019"}re open 7 days a week.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 space-y-5">
                <a href={BRAND.phoneHref} data-testid="booking-phone" className="flex items-center gap-4 group">
                  <span className="grid place-items-center h-11 w-11 rounded-full bg-[#c96c31]/10 text-[#c96c31] group-hover:bg-[#c96c31] group-hover:text-[#f9f6f0] transition-colors">
                    <Phone size={18} />
                  </span>
                  <span className="font-body text-[#2c1e16] group-hover:text-[#c96c31] transition-colors">{BRAND.phone}</span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center h-11 w-11 rounded-full bg-[#c96c31]/10 text-[#c96c31]">
                    <MapPin size={18} />
                  </span>
                  <span className="font-body text-[#2c1e16]">{BRAND.address}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center h-11 w-11 rounded-full bg-[#c96c31]/10 text-[#c96c31]">
                    <Clock size={18} />
                  </span>
                  <span className="font-body text-[#2c1e16]">{BRAND.hours}</span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="arch-frame mt-10 aspect-[4/3] max-w-sm border border-[#2c1e16]/10 overflow-hidden hidden lg:block">
                <img src={IMAGES.g4} alt="Honey twists finish" className="h-full w-full object-cover" />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-[#2c1e16]/10 bg-[#f9f6f0] p-8 md:p-12">
                {done ? (
                  <div className="text-center py-12" data-testid="booking-success">
                    <CheckCircle2 className="mx-auto text-[#c96c31]" size={56} />
                    <h3 className="font-display text-3xl md:text-4xl text-[#2c1e16] mt-6">Request received</h3>
                    <p className="font-body text-[#2c1e16]/70 mt-3 max-w-sm mx-auto">
                      Thank you. We{"\u2019"}ll call you shortly to confirm your appointment.
                    </p>
                    <button
                      data-testid="booking-another-btn"
                      onClick={() => setDone(false)}
                      className="mt-8 rounded-full border border-[#2c1e16]/25 px-7 py-3 font-body text-sm uppercase tracking-wider text-[#2c1e16] hover:border-[#c96c31] hover:text-[#c96c31] transition-all"
                    >
                      Book another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="booking-form">
                    <input
                      type="text"
                      name="website"
                      value={form.website}
                      onChange={update("website")}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Full name</label>
                        <input data-testid="booking-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Your name" />
                      </div>
                      <div>
                        <label className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Phone</label>
                        <input data-testid="booking-phone-input" className={inputCls} value={form.phone} onChange={update("phone")} placeholder="Phone number" />
                      </div>
                      <div>
                        <label className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Email (optional)</label>
                        <input data-testid="booking-email" className={inputCls} value={form.email} onChange={update("email")} placeholder="you@email.com" />
                      </div>
                      <div>
                        <label className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Service</label>
                        <select data-testid="booking-service" className={inputCls} value={form.service} onChange={update("service")}>
                          {SERVICE_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Preferred date</label>
                        <input type="date" data-testid="booking-date" className={inputCls} value={form.preferred_date} onChange={update("preferred_date")} />
                      </div>
                      <div>
                        <label className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Preferred time</label>
                        <input type="time" data-testid="booking-time" className={inputCls} value={form.preferred_time} onChange={update("preferred_time")} />
                      </div>
                    </div>
                    <div className="mt-6">
                      <label className="font-body text-xs uppercase tracking-widest text-[#2c1e16]/50">Notes (optional)</label>
                      <textarea data-testid="booking-notes" rows={3} className={inputCls + " resize-none"} value={form.notes} onChange={update("notes")} placeholder="Length, color, inspiration…" />
                    </div>
                    <button
                      type="submit"
                      data-testid="booking-submit-btn"
                      disabled={loading}
                      className="mt-8 w-full rounded-full bg-[#c96c31] px-8 py-4 font-body text-sm font-medium uppercase tracking-wider text-[#f9f6f0] hover:bg-[#b35d27] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
                    >
                      {loading ? "Sending…" : "Request Appointment"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
